import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import { OverlayTrigger, Tooltip, Container } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import { StoreContext } from '../contexts/StoreContext';
import { FaLanguage, FaStripeS, FaShippingFast, FaEnvelope, FaDatabase, FaPaypal } from 'react-icons/fa';
import './Settings.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;
const ELECTRON_URL = process.env.REACT_APP_ELECTRON_URL;

function Settings() {
  const { token, role } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    admin: {},
    onboarding: {},
    stripeAccountId: '',
    stripeAccountCreatedAt: '',
    stripeOnboardingComplete: '',
    stripeOnboardingCompletedAt: '',
    stripeAccountDetailsSubmitted: '',
    stripeAccountPayoutsEnabled: ''
  });
  const { stores, fetchStores } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const addCaveFromURL = searchParams.get('addcave') === 'true';

  const navigate = useNavigate();

  useEffect(() => { 
    if (token) {
      fetchSettings();
      fetchStores();
    }
  }, [token]);

  useEffect(() => {
    if (addCaveFromURL) {
      handleAddStore();
    }
  }, [addCaveFromURL, stores]);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${STORE_API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error fetching settings.');
      }

      const data = await response.json();
      /*
      const stripeAccount = data.stripe_accounts && data.stripe_accounts.length > 0 ? data.stripe_accounts[0] : {};
  
      setSettings({
        admin: data.admin,
        onboarding: data.onboarding,
        stripeAccountId: stripeAccount.account_id || '',
        stripeAccountCreatedAt: stripeAccount.created_at || '',
        stripeOnboardingComplete: stripeAccount.onboarding_complete || false,
        stripeOnboardingCompletedAt: stripeAccount.onboarding_completed_at || '',
        stripeAccountDetailsSubmitted: (stripeAccount.account && stripeAccount.account.details_submitted) || '',
        stripeAccountPayoutsEnabled: (stripeAccount.account && stripeAccount.account.payouts_enabled) || ''
      });
      */
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLocalizationRedirect = () => {
    navigate('/localization');
  };

  const handleNavigateToStripeSettings = () => {
    navigate('/settings/stripe');
  };

  const handleNavigateToShippingSettings = () => {
    navigate('/settings/shipping');
  };

  const handleNavigateToMessages = () => {
    navigate('/settings/messages');
  };

  const handleNavigateToTrainingSettings = () => {
    navigate('/settings/training');
  };

  const handleNavigateToPaypalSettings = () => {
    navigate('/settings/paypal');
  };

  const handleAddStore = () => {
    navigate('/settings/add-store');
  };

  const handleEditStore = (store) => {
    navigate(`/settings/edit-store/${store._id}`);
  };

  const handleCloseStore = async (storeId) => {
    const userConfirmed = window.confirm("Are you sure you want to shutdown this store?");
    if (!userConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/stores/${storeId}/close`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error closing store.');
      }

      toast.success('Store closed successfully!');
      fetchStores();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeStore = async (storeId) => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/stores/${storeId}/resume`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        throw new Error('Error resuming store.');
      }
  
      toast.success('Store resumed successfully!');
      fetchStores();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderTooltip = (text) => (
    <Tooltip className="d-none d-lg-block">
      {text}
    </Tooltip>
  );

  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Settings</h2>
        <div>
          <button className="btn btn-primary mb-2" onClick={handleAddStore}>Create a New Cave</button>
        </div>
      </div>
      {stores.some(store => !store.is_cave) && (
        <>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <>
                <button className="btn btn-primary me-2 mb-2" onClick={handleLocalizationRedirect}><FaLanguage /></button>
                <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToMessages}><FaEnvelope style={{ marginRight: '5px' }} />Messages</button>
                <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToShippingSettings}><FaShippingFast style={{ marginRight: '5px' }} />Shipping</button>
                <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToStripeSettings}><FaStripeS style={{ marginRight: '5px' }} />Stripe</button>
                <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToPaypalSettings}><FaPaypal style={{ marginRight: '5px' }} />PayPal</button>
                {role === 'super' && (
                  <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToTrainingSettings}><FaDatabase style={{ marginRight: '5px' }} />Training</button>
                )}
              </>
            </div>
          </div>
          <ul className="list-group mb-3">
            {stores.map((store) => (
              <li key={store._id} className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-12 col-md-8 d-flex align-items-center mb-2 mb-md-0">
                    {store.logo ? (
                      <img 
                        src={`${store.logo}`}
                        alt={store.name} 
                        style={{ maxHeight: '2.2rem', maxWidth: '2.2rem', objectFit: 'cover', marginRight: '10px' }} 
                      />
                    ) : (
                      <img 
                        src={`/placeholder.gif`}
                        alt="Placeholder Logo" 
                        style={{ maxHeight: '2.2rem', maxWidth: '2.2rem', objectFit: 'cover', marginRight: '10px' }} 
                      />
                    )}
                    <div>
                      {store.stripe_account_id && store.stripe_account_id !== '' ? (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={renderTooltip('Stripe Account Attached')}
                        >
                          <span>
                            <FaStripeS
                              aria-label="Stripe Attached"
                              style={{
                                color: '#FFFFFF',
                                marginRight: '10px',
                                backgroundColor: '#635BFF',
                                fontSize: '20px',
                                padding: '5px',
                                borderRadius: '3px'
                              }}
                            />
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={renderTooltip('No Stripe Account Attached')}
                        >
                          <span>
                            <FaStripeS
                            aria-label="Stripe Attached"
                            style={{
                              color: '#FFFFFF',
                              marginRight: '10px',
                              backgroundColor: '#CCCCCC',
                              fontSize: '20px',
                              padding: '5px',
                              borderRadius: '3px'
                            }}
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                      <strong>{store.name}</strong> - {store.description}
                    </div>
                  </div>

                  <div className="col-12 col-md-4 d-flex justify-content-md-end">
                    {!store.is_archived ? (
                      <button className="btn btn-secondary btn-sm me-2" onClick={() => handleCloseStore(store._id)}>
                        Close {store.is_cave ? 'Cave' : 'Store'}
                      </button>
                    ) : (
                      <button className="btn btn-success btn-sm me-2" onClick={() => handleResumeStore(store._id)}>
                        Resume {store.is_cave ? 'Cave' : 'Store'}
                      </button>
                    )}
                    <a 
                      href={store.domain_status === 'approved' ? 'https://'+store.domain : `${ELECTRON_URL}/${store.handle}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-primary btn-sm me-2"
                    >
                      Preview
                    </a>
                    <button className="btn btn-primary btn-sm" onClick={() => handleEditStore(store)}>
                      Edit
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {stores.length === 0 && (
        <div className="col-md-12 mt-3">
          <div className="card mb-3 p-3" style={{ border: '2px solid #ff5722' }}>
            <div className="card-body">
              <h5 className="card-title" style={{ color: '#ff5722', fontWeight: 'bold' }}>
                Your default cave is being created
              </h5>
              <p>
                A default cave is automatically created when you sign up. If you don't see it yet, please refresh the page.
              </p>
              <p>
                If you still don't see your cave, you can manually create one:
              </p>
              <Link to="/settings?addcave=true">
                <button className="btn btn-warning mt-2">
                  Create Cave Manually
                </button>
              </Link>
              <p className="mt-3">
                <Link to="/showcase" className="btn btn-primary">
                  Go to Showcase Upload
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}

export default Settings;
